import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { JointCampaignFactory, RewardCampaign } from '@arcxgame/contracts/dist/src/typings'
import { Contract, ContractTransaction } from '@ethersproject/contracts'
import dayjs from 'dayjs'
import { BigNumber, Signer } from 'ethers'
import { Network } from 'src/config'
import { getReadableNumber } from 'src/helpers/getReadableNumber'
import { GraphService, PositionWithSynth } from 'src/services/GraphService'
import ArcFarm from './ArcFarm'

export class RewardCampaignFarm extends ArcFarm {
  constructor(
    name: string,
    platform: string,
    pair: string,
    pairLink: string,
    note: string,
    signer: Signer,
    network: Network,
    public contract: RewardCampaign,
    client: ApolloClient<NormalizedCacheObject>,
  ) {
    super(name, platform, pair, pairLink, note, signer, network, contract, client)
  }

  /* ========== Getters ========== */

  hasDebtRequirement(): boolean {
    return true
  }

  async getDebtRequirementString(): Promise<string> {
    const signerAddress = await this.signer.getAddress()
    let debtRequirement
    try {
      // For some reason, the RewardCampaign class throws an error for farm 4,
      // unless we append the ABI manually like below
      const contract = new Contract(
        this.contract.address,
        [
          'function stakers(address staker) external view returns(uint256, uint256, uint256, uint256, uint256, uint256, uint256)',
        ],
        this.signer,
      )
      const stakerDetails = await contract.stakers(signerAddress)
      debtRequirement = stakerDetails.debtSnapshot
    } catch (err) {
      console.error('Error calling contract.stakers()', err)
      return 'N/A'
    }

    return `${getReadableNumber(debtRequirement)} STABLEx`
  }

  async getVestingEndDateString(): Promise<string> {
    try {
      const vestingEndDate = await this.contract.vestingEndDate()
      return dayjs(vestingEndDate.toNumber() * 1000).format('DD / MM / YYYY')
    } catch (err) {
      console.error('Error calling contract.vestingEndDate()', err)
      return 'N/A'
    }
  }

  /**
   * Returns the the position with the highest minted debt for the collateral of this farm
   */
  async getLargestDebtPosition(): Promise<PositionWithSynth | null> {
    if (!this.hasDebtRequirement()) {
      return null
    }

    const graphService = new GraphService(this.client)
    let stateContracts
    try {
      stateContracts = await this.contract.getAllApprovedStateContracts()
    } catch (err) {
      console.error(
        'Error calling contract.getAllApprovedStateContracts(). Probably an old pool, trying contract.stateContract()',
      )
    }

    if (!stateContracts) {
      try {
        const singleStateContract = JointCampaignFactory.connect(this.contract.address, this.signer)
        stateContracts = [await singleStateContract.stateContract()]
      } catch (err) {
        console.error('Error calling contract.stateContract()', err)
        return null
      }
    }

    const signerAddress = await this.signer.getAddress()

    const largestPosition = await graphService.fetchHighestPositionOfSynths(
      signerAddress,
      stateContracts,
    )

    return largestPosition
  }

  async getAvailableToStake(): Promise<BigNumber> {
    const stakingToken = await this.getStakingToken()

    const currentDebt = await this.getCurrentDebt()

    let debtToStakeRatio: BigNumber
    try {
      debtToStakeRatio = BigNumber.from(await this.contract.debtToStake())
    } catch (err) {
      console.error('Error while calling contract.debtToStake()', err)
      return BigNumber.from(0)
    }

    const stakedBalance = await this.getStakedBalance()
    const stakableBalance = await stakingToken.balanceOf(await this.signer.getAddress())

    return this._getAvailableToStake(
      currentDebt,
      debtToStakeRatio,
      stakedBalance,
      stakableBalance,
      true,
    )
  }

  /* ========== Modifiers ========== */

  async stake(amount: BigNumber): Promise<ContractTransaction> {
    const stakableMax = await this.getAvailableToStake()

    if (amount.gt(stakableMax)) {
      throw Error('You do not have minted enough debt!')
    }

    const stakingToken = await this.getStakingToken()
    const allowance = await stakingToken.allowance(
      await this.signer.getAddress(),
      this.contract.address,
    )

    if (allowance.lt(amount)) {
      try {
        const tx = await stakingToken.approve(this.contract.address, amount.toString())
        await tx.wait()
      } catch {
        // If an approval is failing it's probably because it's a curve token LP token wanting
        // the approval to be set to 0 smh...
        const resetApproval = await stakingToken.approve(this.contract.address, 0)
        await resetApproval.wait()

        const approveTx = await stakingToken.approve(this.contract.address, amount.toString())
        await approveTx.wait()
      }
    }

    try {
      const position = await this.getLargestDebtPosition()

      const stakeTx = await this.contract.stake(amount, position.id.split('-')[1], position.synth)
      return stakeTx
    } catch (err) {
      console.error(err)
      throw Error('An error occured during staking')
    }
  }
}
